import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Trophy,
  Flame,
  Calendar,
  Eye,
  EyeOff,
  Loader2,
  Medal
} from 'lucide-react';

const API = '/api';

const LeaderboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/leaderboard?period=${period}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      return;
    }
    try {
      await axios.post(
        `${API}/leaderboard/visibility`,
        null,
        {
          params: { hidden: !isHidden },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setIsHidden(!isHidden);
      toast.success(isHidden ? 'Artık sıralamada görünüyorsunuz' : 'Sıralamadan gizlendiniz');
    } catch (error) {
      toast.error('Ayar değiştirilemedi');
    }
  };

  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-500';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-amber-600';
    return 'text-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-yellow-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Trophy className="h-8 w-8 text-yellow-500" />
            Liderlik Tablosu
          </h1>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 mb-6">
          {['weekly', 'monthly', 'all_time'].map((p) => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {p === 'weekly' ? 'Haftalık' : p === 'monthly' ? 'Aylık' : 'Tüm Zamanlar'}
            </Button>
          ))}
        </div>

        {/* Visibility Toggle */}
        <Card className="mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isHidden ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-green-500" />}
              <span>Sıralamada görünürlük</span>
            </div>
            <Switch checked={!isHidden} onCheckedChange={toggleVisibility} />
          </CardContent>
        </Card>

        {/* My Stats */}
        {data?.my_stats && (
          <Card className="mb-6 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Senin İstatistiklerin</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/20 rounded-lg p-4 text-center">
                  <Calendar className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{data.my_stats.checkin_count}</p>
                  <p className="text-sm text-purple-200">Check-in</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4 text-center">
                  <Flame className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{data.my_stats.current_streak}</p>
                  <p className="text-sm text-purple-200">Streak</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-in Leaders */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-500" />
              En Çok Check-in
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.checkin_leaders?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
            ) : (
              <div className="space-y-3">
                {data?.checkin_leaders?.map((leader, index) => (
                  <div key={leader._id || index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <div className="w-8 text-center">
                      {index < 3 ? (
                        <Medal className={`h-6 w-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-gray-400 font-medium">{index + 1}</span>
                      )}
                    </div>
                    <Avatar>
                      <AvatarImage src={leader.user?.avatar} />
                      <AvatarFallback>{leader.user?.name?.[0] || '?'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{leader.user?.name || 'Kullanıcı'}</p>
                    </div>
                    <Badge variant="outline">{leader.checkin_count} check-in</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Streak Leaders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              En Uzun Streak
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.streak_leaders?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
            ) : (
              <div className="space-y-3">
                {data?.streak_leaders?.map((leader, index) => (
                  <div key={leader.user_id || index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <div className="w-8 text-center">
                      {index < 3 ? (
                        <Medal className={`h-6 w-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-gray-400 font-medium">{index + 1}</span>
                      )}
                    </div>
                    <Avatar>
                      <AvatarImage src={leader.user?.avatar} />
                      <AvatarFallback>{leader.user?.name?.[0] || '?'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{leader.user?.name || 'Kullanıcı'}</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700">
                      <Flame className="h-3 w-3 mr-1" />
                      {leader.current_streak} gün
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeaderboardPage;
